import { Head, Link } from "@inertiajs/react"
import { Archive, ArrowLeft, ArrowUpRight, Clock, Gauge } from "lucide-react"
import type { ReactNode } from "react"

import { DecadeBadge } from "@/components/directory/decade-badge"
import { Screenshot } from "@/components/directory/screenshot"
import { SiteCard } from "@/components/directory/site-card"
import { StatusPill } from "@/components/directory/status-pill"
import { UptimeStrip } from "@/components/directory/uptime-strip"
import { withPublicLayout } from "@/layouts/public-layout"
import { rootPath } from "@/routes"
import type { SiteCard as SiteCardData, SiteDetail } from "@/types/site"

interface Props {
  site: SiteDetail
  neighbors: SiteCardData[]
}

const dateFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
})

function relative(iso: string | null) {
  if (!iso) return "never"
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)} days ago`
}

export default function SiteShow({ site, neighbors }: Props) {
  const archived = site.status === "dead" || site.status === "parked"
  const visitHref = archived ? site.wayback_url : site.url

  return (
    <>
      <Head title={site.title} />

      <div className="mx-auto max-w-[1200px] px-4 pt-8 sm:px-6">
        <Link
          href={rootPath()}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="size-4" /> All sites
        </Link>

        <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div>
            <a
              href={visitHref}
              className="group block overflow-hidden rounded-2xl border shadow-[0_24px_60px_-30px_rgb(28_25_23/0.45)]"
            >
              <div className="bg-muted flex items-center gap-1.5 border-b px-4 py-2.5">
                <span className="size-2.5 rounded-full bg-[#ff5f57]" />
                <span className="size-2.5 rounded-full bg-[#febc2e]" />
                <span className="size-2.5 rounded-full bg-[#28c840]" />
                <span className="bg-card text-muted-foreground ml-3 flex-1 truncate rounded-md px-3 py-1 font-mono text-xs">
                  {site.host}
                </span>
              </div>
              <Screenshot
                src={site.screenshot_url}
                host={site.host}
                source={site.screenshot_source}
                alt={`Screenshot of ${site.host}`}
                eager
                className="transition-transform duration-500 group-hover:scale-[1.01]"
              />
            </a>
            {site.screenshot_captured_at && (
              <p className="text-muted-foreground mt-2 text-xs">
                Screenshot taken{" "}
                {dateFormat.format(new Date(site.screenshot_captured_at))}
                {site.screenshot_source === "wayback" &&
                  " from an archived copy"}
              </p>
            )}
          </div>

          <aside className="space-y-6">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill status={site.status} />
                <DecadeBadge decade={site.decade} />
              </div>
              <h1 className="text-3xl leading-tight font-extrabold tracking-tight text-balance">
                {site.title}
              </h1>
              <p className="text-muted-foreground font-mono text-sm">
                {site.host}
              </p>
              {site.description && (
                <p className="text-pretty">{site.description}</p>
              )}
            </div>

            <a
              href={visitHref}
              className="bg-brand hover:bg-brand-hover flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold text-white transition-transform active:scale-[0.98]"
            >
              {archived ? (
                <>
                  <Archive className="size-4" /> Visit the archived version
                </>
              ) : (
                <>
                  Visit {site.host} <ArrowUpRight className="size-4" />
                </>
              )}
            </a>
            {archived && (
              <p className="text-muted-foreground -mt-3 text-xs">
                The live domain no longer serves this site, so this opens the
                Wayback Machine.
              </p>
            )}

            <dl className="divide-y rounded-xl border text-sm">
              {site.launched_on && (
                <Row label="Launched">
                  {dateFormat.format(new Date(site.launched_on))}
                </Row>
              )}
              {site.author_name && (
                <Row label="Made by">
                  {site.author_url ? (
                    <a
                      href={site.author_url}
                      className="underline underline-offset-4"
                      rel="noopener"
                    >
                      {site.author_name}
                    </a>
                  ) : (
                    site.author_name
                  )}
                </Row>
              )}
              <Row label="Uptime (30d)">
                {site.uptime_30d === null ? "—" : `${site.uptime_30d}%`}
              </Row>
              <Row label="Last check">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="text-muted-foreground size-3.5" />
                  {relative(site.last_checked_at)}
                </span>
              </Row>
              {site.last_response_ms !== null && site.status === "up" && (
                <Row label="Response">
                  <span className="inline-flex items-center gap-1.5">
                    <Gauge className="text-muted-foreground size-3.5" />
                    {site.last_response_ms} ms
                  </span>
                </Row>
              )}
              {site.last_error && site.status !== "up" && (
                <Row label="Problem">{site.last_error}</Row>
              )}
            </dl>
          </aside>
        </div>

        <section className="mt-12">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-bold">Recent checks</h2>
            <span className="text-muted-foreground text-xs">
              Hourly, oldest to newest
            </span>
          </div>
          <UptimeStrip checks={site.checks} />
        </section>

        {neighbors.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-5 text-xl font-bold">
              More single-serving sites
            </h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-6">
              {neighbors.map((neighbor) => (
                <SiteCard key={neighbor.slug} site={neighbor} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  )
}

SiteShow.layout = withPublicLayout
